import { useFormContext } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import Cookies from "js-cookie";

import { FormValues } from "../new-employee-form";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command/command";
// import { HelperButton } from "@/components/ui/helper-button/helper-button";
// import { Input } from "@/components/ui/input/input";
import {
  getAllEmployeesQueryOptions,
  getAllHolidayListQueryOptions,
} from "@/config/queries/query-options";
import { Button } from "@/components/ui/button/button";
import { cn } from "@/lib/utils";
import { CaretSortIcon } from "@radix-ui/react-icons";
import { CheckIcon } from "lucide-react";
import { HelperButton } from "@/components/ui/helper-button/helper-button";
import { Input } from "@/components/ui/input/input";

interface NewEmployeeAttendanceLeavesProps {
  isPending: boolean;
}

// ? TAB Attendance & Leaves
// ! holidayList: string (Object ID)
// ! leaveApprover: string (Object ID),
// ! allowanceApprover: string (Object ID),
// ! loanApprover: string (Object ID),
// ? TAB Attendance & Leaves

export const NewEmployeeAttendanceLeaves = ({
  isPending,
}: NewEmployeeAttendanceLeavesProps) => {
  const form = useFormContext<FormValues>();
  const user = Cookies.get("key") as string;

  const { data, isFetching } = useQuery(getAllEmployeesQueryOptions(user));

  const employeesData = useMemo(() => {
    if (isPending || isFetching) {
      return [];
    }

    if (data === undefined || data?.employees === undefined) {
      return [];
    }

    const result = data?.employees?.map((emp) => {
      return {
        value: emp?._id,
        label: emp?.name,
        permissions: emp?.permissions,
      };
    });

    return result;
  }, [data, isPending, isFetching]);

  const leaveApprovers = useMemo(() => {
    const result = employeesData?.filter((emp) => {
      const does =
        emp?.permissions?.["1"]?.includes("LeaveApplication") &&
        emp?.permissions?.["2"]?.includes("LeaveApplication");

      return does;
    });

    return result;
  }, [employeesData]);

  const allowanceApprovers = useMemo(() => {
    const result = employeesData?.filter((emp) => {
      const does =
        emp?.permissions?.["1"]?.includes("Allowance") &&
        emp?.permissions?.["2"]?.includes("Allowance");

      return does;
    });

    return result;
  }, [employeesData]);

  const loanApprovers = useMemo(() => {
    const result = employeesData?.filter((emp) => {
      const does =
        emp?.permissions?.["1"]?.includes("Loan") &&
        emp?.permissions?.["2"]?.includes("Loan");

      return does;
    });

    return result;
  }, [employeesData]);

  const {
    data: holidayListData,
    isPending: isHolidayListPending,
    isFetching: isHolidayListFetching,
  } = useQuery(getAllHolidayListQueryOptions(user));

  const holidaysListData = useMemo(() => {
    if (isPending || isFetching) {
      return [];
    }

    if (
      holidayListData === undefined ||
      holidayListData?.foundLists === undefined
    ) {
      return [];
    }

    const result = holidayListData?.foundLists?.map((emp) => {
      return {
        value: emp?._id,
        label: emp?.name,
      };
    });

    return result;
  }, [holidayListData, isHolidayListPending, isHolidayListFetching]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
      <FormField
        control={form.control}
        name="holidayList"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>Holiday List</FormLabel>
            <Popover>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    variant="outline"
                    role="combobox"
                    className={cn(
                      "w-full justify-between",
                      !field.value && "text-muted-foreground"
                    )}
                  >
                    {field.value
                      ? holidaysListData.find(
                          (holidaylist) => holidaylist.value === field.value
                        )?.label
                      : "Select holiday list"}
                    <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput
                    placeholder="Search holiday list..."
                    className="h-9"
                  />
                  <CommandEmpty>No holiday list found.</CommandEmpty>
                  <CommandGroup>
                    <CommandList>
                      {holidaysListData.map((holidaylist) => (
                        <CommandItem
                          value={holidaylist.label}
                          key={holidaylist.value}
                          onSelect={() => {
                            form.setValue("holidayList", holidaylist.value);
                          }}
                        >
                          {holidaylist.label}
                          <CheckIcon
                            className={cn(
                              "ml-auto h-4 w-4",
                              holidaylist.value === field.value
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandList>
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="paidLeavesTotal"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-1">
              Total Paid Leaves
              <HelperButton>
                <p>Total Paid Leaves of the employee</p>
              </HelperButton>
            </FormLabel>
            <FormControl>
              <Input type="number" {...field} disabled={isPending} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="paidLeavesRemaining"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-1">
              Paid Leaves Remaining
              <HelperButton>
                <p>Total Paid Leaves of the employee</p>
              </HelperButton>
            </FormLabel>
            <FormControl>
              <Input type="number" {...field} disabled={isPending} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="leaveApprover"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>Leave Approver</FormLabel>
            <Popover>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    variant="outline"
                    role="combobox"
                    className={cn(
                      "w-full justify-between",
                      !field.value && "text-muted-foreground"
                    )}
                  >
                    {field.value
                      ? leaveApprovers.find(
                          (employee) => employee.value === field.value
                        )?.label
                      : "Select leave approver"}
                    <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput
                    placeholder="Search leave approver..."
                    className="h-9"
                  />
                  <CommandEmpty>No leave approver found.</CommandEmpty>
                  <CommandGroup>
                    <CommandList>
                      {leaveApprovers.map((employee) => (
                        <CommandItem
                          value={employee.label}
                          key={employee.value}
                          onSelect={() => {
                            form.setValue("leaveApprover", employee.value);
                          }}
                        >
                          {employee.label}
                          <CheckIcon
                            className={cn(
                              "ml-auto h-4 w-4",
                              employee.value === field.value
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandList>
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="allowanceApprover"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>Allowance Approver</FormLabel>
            <Popover>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    variant="outline"
                    role="combobox"
                    className={cn(
                      "w-full justify-between",
                      !field.value && "text-muted-foreground"
                    )}
                  >
                    {field.value
                      ? allowanceApprovers.find(
                          (employee) => employee.value === field.value
                        )?.label
                      : "Select allowance approver"}
                    <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput
                    placeholder="Search allowance approver..."
                    className="h-9"
                  />
                  <CommandEmpty>No allowance approver found.</CommandEmpty>
                  <CommandGroup>
                    <CommandList>
                      {allowanceApprovers.map((employee) => (
                        <CommandItem
                          value={employee.label}
                          key={employee.value}
                          onSelect={() => {
                            form.setValue("allowanceApprover", employee.value);
                          }}
                        >
                          {employee.label}
                          <CheckIcon
                            className={cn(
                              "ml-auto h-4 w-4",
                              employee.value === field.value
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandList>
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="loanApprover"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>Loan Approver</FormLabel>
            <Popover>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    variant="outline"
                    role="combobox"
                    className={cn(
                      "w-full justify-between",
                      !field.value && "text-muted-foreground"
                    )}
                  >
                    {field.value
                      ? loanApprovers.find(
                          (employee) => employee.value === field.value
                        )?.label
                      : "Select loan approver"}
                    <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput
                    placeholder="Search loan approver..."
                    className="h-9"
                  />
                  <CommandEmpty>No loan approver found.</CommandEmpty>
                  <CommandGroup>
                    <CommandList>
                      {loanApprovers.map((employee) => (
                        <CommandItem
                          value={employee.label}
                          key={employee.value}
                          onSelect={() => {
                            form.setValue("loanApprover", employee.value);
                          }}
                        >
                          {employee.label}
                          <CheckIcon
                            className={cn(
                              "ml-auto h-4 w-4",
                              employee.value === field.value
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandList>
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
